import { useEffect, useMemo } from 'react';
import { useCourseScopeStore } from '@/store/courseScopeStore';
import { useCourses, useTermCourses } from '@/hooks/useGradebookQueries';
import { useTermsInSelectedYear, resolveTermId } from '@/hooks/useTermsInSelectedYear';
import { useGradeLevelFormat } from '@/hooks/useGradeLevels';
import { groupByGradeSpan, type GradeSpanGroup } from '@/lib/gradeLevels';
import type { Course, Term } from '@/types/api';

/**
 * Which class the session is pointed at: a term, a course, and the section that
 * is the two of them together.
 *
 * The stored ids are only ever a *candidate*. Everything below resolves them
 * against the lists actually fetched, and nothing writes back during render —
 * a stale id simply does not win, which is what keeps a year switch, a course
 * deletion and a first visit all landing on a gradebook that exists rather
 * than on an empty one.
 */
export interface CourseScope {
  terms: Term[];
  /** The term in scope, resolved: the stored one if it is still in the year. */
  term: Term | null;
  /** The courses taught in that term, grouped the way the old rail grouped. */
  courseGroups: GradeSpanGroup<Course>[];
  /** The course in scope, resolved against the courses of `term`. */
  course: Course | null;
  /** The section — course × term — every gradebook query is keyed by. */
  termCourseId: string | null;
  selectTerm: (termId: string) => void;
  selectCourse: (courseId: string) => void;
  isLoading: boolean;
}

export function useCourseScope(): CourseScope {
  const selectedTermId = useCourseScopeStore((state) => state.selectedTermId);
  const selectedCourseId = useCourseScopeStore((state) => state.selectedCourseId);
  const setSelectedTermId = useCourseScopeStore((state) => state.setSelectedTermId);
  const setSelectedCourseId = useCourseScopeStore((state) => state.setSelectedCourseId);

  const { terms, isLoading: isLoadingTerms } = useTermsInSelectedYear();
  const { data: courses = [], isLoading: isLoadingCourses } = useCourses();
  const { data: termCourses = [], isLoading: isLoadingTermCourses } = useTermCourses();
  const { labels: gradeLabels } = useGradeLevelFormat();

  // A term stranded by a year switch belongs to the year just left; keeping it
  // would show that year's work under the new year's name.
  const termId = resolveTermId(terms, selectedTermId);
  const term = terms.find((candidate) => candidate.id === termId) ?? null;

  // The courses on offer: those actually taught in the term in scope. Keyed off
  // the resolved term rather than the stored one, so a year with no terms at
  // all offers nothing instead of falling through to every course in the school.
  const coursesForTerm = useMemo(() => {
    if (!termId) return [];
    const courseIds = new Set(
      termCourses.filter((tc) => tc.termId === termId).map((tc) => tc.courseId)
    );
    return courses.filter((course) => courseIds.has(course.id));
  }, [courses, termCourses, termId]);

  // Grouped by the grades each course covers rather than run as one flat list
  // of every course in the school: the menu is how a teacher finds the class
  // they are about to grade.
  const courseGroups = useMemo(
    () => groupByGradeSpan(coursesForTerm, gradeLabels),
    [coursesForTerm, gradeLabels]
  );

  // Resolution follows the menu's own order, not the order the API happened to
  // return: the course that opens selected has to be the one at the top of the
  // list, or the pill names a course buried mid-menu.
  const course =
    coursesForTerm.find((candidate) => candidate.id === selectedCourseId) ??
    courseGroups[0]?.items[0] ??
    null;

  const termCourseId =
    termId && course
      ? termCourses.find((tc) => tc.termId === termId && tc.courseId === course.id)?.id ??
        null
      : null;

  // Written back only once the data has settled on a different answer, and only
  // from an effect: a store write during render is a render loop waiting for a
  // second subscriber. This is what makes the choice stick — without it the
  // pill would keep re-resolving a course the user never picked.
  useEffect(() => {
    if (termId && termId !== selectedTermId) setSelectedTermId(termId);
  }, [termId, selectedTermId, setSelectedTermId]);

  useEffect(() => {
    if (course && course.id !== selectedCourseId) setSelectedCourseId(course.id);
  }, [course, selectedCourseId, setSelectedCourseId]);

  return {
    terms,
    term,
    courseGroups,
    course,
    termCourseId,
    selectTerm: setSelectedTermId,
    selectCourse: setSelectedCourseId,
    // `/api/term-courses` counts too: it is what says which courses run in the
    // term, so until it lands every course is filtered out and the scope looks
    // empty. Leaving it out flashed "No courses in this term" over a gradebook
    // that has them, and "No course" on the pill, for as long as that one
    // request lagged the other two.
    isLoading: isLoadingTerms || isLoadingCourses || isLoadingTermCourses,
  };
}

/**
 * Declares that the page on screen is scoped to a course, which is what puts
 * the Term and Course pills on the topbar. Released on unmount, so the pills
 * leave with the page that owns them.
 */
export function useCourseScopePills(): void {
  const acquireScope = useCourseScopeStore((state) => state.acquireScope);
  const releaseScope = useCourseScopeStore((state) => state.releaseScope);

  useEffect(() => {
    acquireScope();
    return releaseScope;
  }, [acquireScope, releaseScope]);
}
